type PoolGoal = { name: string | null; role: string };

/**
 * The events an objective picker offers, by how the website mapped them. Both pickers offer every
 * group, because which event matters most varies by experiment: one tests checkout and judges on
 * purchase, the next tests the product page and judges on add to cart. The mapping only decides
 * what is offered first and what new experiments are seeded with.
 *
 * - `primary` / `secondary`: the website's two pools.
 * - `notMapped`: events the website has seen but left at the default role, `ignored`. Offered last;
 *   picking one asks whether to map it too (useObjectiveMapping).
 * - `untracked`: selected names that are not among the website's events at all (a deleted row, a
 *   legacy name). They still have to show, or a control renders blank and the next interaction
 *   silently drops what a running experiment is measured on.
 */
export type ObjectiveOptionGroups = {
  primary: string[];
  secondary: string[];
  notMapped: string[];
  untracked: string[];
};

type ObjectiveOptionGroupsParams = { goals: readonly PoolGoal[]; selected: readonly string[] };

const namesIn = ({ goals, role }: { goals: readonly PoolGoal[]; role: string }): string[] => [
  ...new Set(
    goals
      .filter((goal) => goal.role === role)
      .map((goal) => goal.name?.trim() ?? '')
      .filter((name) => name.length > 0),
  ),
];

export function objectiveOptionGroups({ goals, selected }: ObjectiveOptionGroupsParams): ObjectiveOptionGroups {
  const primary = namesIn({ goals, role: 'primary' });
  const secondary = namesIn({ goals, role: 'secondary' }).filter((name) => !primary.includes(name));
  const notMapped = namesIn({ goals, role: 'ignored' }).filter(
    (name) => !primary.includes(name) && !secondary.includes(name),
  );
  const known = new Set([...primary, ...secondary, ...notMapped]);
  const untracked = [...new Set(selected)].filter((name) => !known.has(name));
  return { primary, secondary, notMapped, untracked };
}

/** True when the website has the event in neither pool: ignored, or not among its events at all. */
export const isUnmapped = (groups: ObjectiveOptionGroups, name: string): boolean =>
  groups.notMapped.includes(name) || groups.untracked.includes(name);
